import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_URL}/v1`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Read tokens from storage but reject the literal string "undefined" / "null"
// that older bugs may have written. Without this, the request interceptor
// happily attaches `Authorization: Bearer undefined` and the backend 401s
// every call.
const _readStoredToken = (key: string): string | null => {
  const v = localStorage.getItem(key);
  if (!v || v === 'undefined' || v === 'null') {
    localStorage.removeItem(key);
    return null;
  }
  return v;
};

let accessToken: string | null = _readStoredToken('accessToken');
let refreshToken: string | null = _readStoredToken('refreshToken');
let refreshPromise: Promise<void> | null = null; // Mutex for concurrent refresh

export const setTokens = (access: string | undefined | null, refresh: string | undefined | null) => {
  // Defensive: never store undefined/empty as a "token". Anything calling this
  // with no real tokens is a bug; treat it as clear instead of corrupting state.
  if (!access || !refresh) {
    clearTokens();
    return;
  }
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem('accessToken', access);
  localStorage.setItem('refreshToken', refresh);
};

export const clearTokens = () => {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

export const getAccessToken = () => accessToken;

// v6.3.32: lets the Socket.IO client refresh an expired access token before a
// reconnect attempt. Reuses the same mutex as the axios interceptor.
export const tryRefreshTokens = async (): Promise<boolean> => {
  if (!refreshToken) return false;
  if (refreshPromise) {
    try { await refreshPromise; return true; } catch { return false; }
  }
  refreshPromise = (async () => {
    const response = await axios.post(`${API_URL}/v1/auth/refresh`, { refresh_token: refreshToken });
    const { access_token, refresh_token } = response.data;
    setTokens(access_token, refresh_token);
  })();
  try {
    await refreshPromise;
    return true;
  } catch {
    return false;
  } finally {
    refreshPromise = null;
  }
};

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    if (error.response?.status === 401) {
      // Check for specific session expiry reasons
      const sessionExpired = error.response.headers?.['x-session-expired'];
      
      if (sessionExpired === 'dual-login') {
        clearTokens();
        window.location.href = '/login?reason=dual-login';
        return Promise.reject(error);
      }
      
      if (sessionExpired === 'inactivity') {
        clearTokens();
        window.location.href = '/login?reason=inactivity';
        return Promise.reject(error);
      }
      
      // Try to refresh the token if we haven't already
      if (refreshToken && !originalRequest._retry) {
        originalRequest._retry = true;
        
        // Use a mutex to prevent multiple concurrent refresh requests.
        // If another request is already refreshing, wait for it.
        if (refreshPromise) {
          try {
            await refreshPromise;
            // Tokens were refreshed by the other request - retry with new token
            if (accessToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }
            return apiClient(originalRequest);
          } catch {
            // Other refresh failed, don't redirect (it already did)
            return Promise.reject(error);
          }
        }
        
        refreshPromise = (async () => {
          const response = await axios.post(`${API_URL}/v1/auth/refresh`, { refresh_token: refreshToken });
          const { access_token, refresh_token } = response.data;
          setTokens(access_token, refresh_token);
        })();
        
        try {
          await refreshPromise;
          refreshPromise = null;
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return apiClient(originalRequest);
        } catch (refreshError: any) {
          refreshPromise = null;
          const refreshSessionExpired = refreshError.response?.headers?.['x-session-expired'];
          if (refreshSessionExpired === 'dual-login') {
            clearTokens();
            window.location.href = '/login?reason=dual-login';
          } else {
            clearTokens();
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }
      
      // No refresh token or already retried - only redirect if not already refreshing
      if (!refreshPromise) {
        clearTokens();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
